import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PdfCanvasViewer } from '@/components/common/PdfCanvasViewer';
import { usePillsForMe, useCorporateDocumentsForMe } from '@/api/recognition';
import type { DocumentationPillDto } from '@/api/recognition';

// ─────────────────────────────────────────────────────────────────────────────
// Extraído de HomeRightRail.tsx (BUG 2026-09-04): el rail de escritorio (`.home-right-rail`) y el
// overlay "Más → Documentos" del nav móvil frontline (MobileNav.tsx) tienen que mostrar EXACTAMENTE
// las mismas pills — antes "Documentos" en móvil abría un único PDF hardcodeado (reglas legales) sin
// enterarse de que existían más pills configuradas por un admin (video, otros documentos, urls), que
// quedaban con la nueva instancia del rail montado ni existente en la zona móvil frontline. Ese
// contenido era, en la práctica, inalcanzable desde un celular. La consulta de datos
// (`useDocumentationPills`), la activación de una pill y su modal de visor (`usePillActivation` +
// `PillActivationModal`) viven aquí una sola vez; cada superficie (rail de escritorio, lista de
// "Más" en móvil) solo aporta su propio layout de botones.
// ─────────────────────────────────────────────────────────────────────────────

/** Texto localizado con degradación: idioma actual → español → primero disponible → vacío. */
export function pickTitle(map: Record<string, string> | undefined, lang: string): string {
  if (!map) return '';
  return map[lang]?.trim() || map.es?.trim() || Object.values(map).find((v) => v?.trim()) || '';
}

/** true solo si es una URL absoluta http/https (defensa; el backend ya valida el esquema al guardar). */
function isHttpUrl(url: string | null | undefined): url is string {
  return typeof url === 'string' && /^https?:\/\//i.test(url.trim());
}

/**
 * true si la URL es enlazable desde una pill `url`/`video`: absoluta http(s) O ruta root-relative del
 * MISMO origen (`/assets/...`), para poder enlazar assets internos del web (§24.4.9). Rechaza
 * protocol-relative (`//host`) y su variante con backslash (`/\host`, que el navegador normaliza a
 * `//host` → otro origen). Espeja `PillValidation.IsValidPillUrl` del backend.
 */
function isPillLinkUrl(url: string | null | undefined): url is string {
  if (typeof url !== 'string') return false;
  const u = url.trim();
  if (u.startsWith('/')) return u.length === 1 || (u[1] !== '/' && u[1] !== '\\');
  return /^https?:\/\//i.test(u);
}

/**
 * Normaliza una URL de video a su forma embebible (Vimeo / YouTube). Si no reconoce el proveedor,
 * devuelve la URL tal cual (se asume ya embebible).
 */
function toEmbedUrl(url: string): string {
  const vimeo = /vimeo\.com\/(?:video\/)?(\d+)/.exec(url);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
  const yt = /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]+)/.exec(url);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  return url;
}

/** true si la URL apunta a un PDF (por la extensión del path, ignorando el query/fragment del SAS). */
function isPdfUrl(url: string | null | undefined): url is string {
  if (typeof url !== 'string') return false;
  const path = url.split('?')[0].split('#')[0];
  return /\.pdf$/i.test(path);
}

/**
 * Pills activas y visibles para el usuario (filtradas por OU en el backend), listas para pintar.
 * `activePills` (antes de descartar las no-renderizables) es lo que decide si el rail/la lista
 * existe en absoluto — una pill `documento`/`url`/`video` sin URL utilizable ya no debería ni
 * pasar de aquí, pero el conteo de "hay algo configurado" no depende de esa validación de URL.
 */
export function useDocumentationPills(lang: string) {
  const pillsQuery = usePillsForMe();
  const activePills = (pillsQuery.data ?? [])
    .filter((p) => p.isActive)
    .sort((a, b) => a.orden - b.orden); // respeta el orden configurado (no confía en el server)
  const hasDocPills = activePills.some((p) => p.tipo === 'documento');
  // Solo se piden los PDFs corporativos si alguna pill los referencia (evita una llamada extra).
  const corpQuery = useCorporateDocumentsForMe(lang, hasDocPills);
  // Solo se mapean los docs con URL utilizable (SAS re-firmado); un id presente ⇒ enlace vivo.
  const docUrlById = new Map(
    (corpQuery.data ?? []).filter((d) => isHttpUrl(d.url)).map((d) => [d.id, d.url as string] as const),
  );
  const isRenderable = (p: DocumentationPillDto): boolean =>
    p.tipo === 'documento' || isPillLinkUrl(p.url); // url | video (acepta root-relative del mismo origen)

  return {
    isFetched: pillsQuery.isFetched,
    activePills,
    pills: activePills.filter(isRenderable),
    docUrlById,
  };
}

/**
 * Estado + acción de "abrir una pill": resuelve a qué corresponde el toque (pestaña nueva para una
 * `url` que no es PDF, o el modal del visor para documento/video/PDF) y guarda el estado del modal
 * (PDF actualmente abierto, error de carga). Cada superficie decide cuándo montar
 * `<PillActivationModal>` con el resultado.
 */
export function usePillActivation(docUrlById: Map<string, string | null>) {
  const [openPill, setOpenPill] = useState<DocumentationPillDto | null>(null);
  const [pdfLoadError, setPdfLoadError] = useState(false);

  const activate = (pill: DocumentationPillDto) => {
    // `url` que NO es un PDF (una página web) → pestaña nueva: un sitio externo no siempre se puede
    // embeber en un iframe. Todo lo demás abre el MODAL: `documento` y `video` como siempre, y
    // también `url` que apunta a un PDF (abre el modal con el PDF adentro, no lo descarga).
    if (pill.tipo === 'url' && !isPdfUrl(pill.url)) {
      if (pill.url) window.open(pill.url, '_blank', 'noopener,noreferrer');
      return;
    }
    setPdfLoadError(false);
    setOpenPill(pill);
  };

  const close = () => setOpenPill(null);

  // URL del PDF a visualizar en el modal: doc corporativo (SAS re-firmado por id) o `url` directa a
  // un PDF. Para `video`/`url` web queda null (no se visualiza como PDF).
  const pdfUrl = !openPill
    ? null
    : openPill.tipo === 'documento' && openPill.targetDocumentId
      ? docUrlById.get(openPill.targetDocumentId) ?? null
      : openPill.tipo === 'url' && isPdfUrl(openPill.url)
        ? openPill.url
        : null;

  // Descarga OPCIONAL del PDF que se está visualizando. El visor (PdfCanvasViewer) muestra el PDF
  // en <canvas> a propósito para que NUNCA se descargue solo; este botón da la descarga explícita
  // "si quieren". Se baja por fetch+blob para forzar la descarga aunque el navegador tenga la
  // política de "abrir PDF" (o al revés); si el fetch falla (CORS/red) se abre en pestaña.
  const downloadDoc = async (fileTitle: string) => {
    if (!pdfUrl) return;
    try {
      const resp = await fetch(pdfUrl);
      const blob = await resp.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objUrl;
      a.download = `${fileTitle || 'documento'}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objUrl);
    } catch {
      window.open(pdfUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return { openPill, activate, close, pdfUrl, pdfLoadError, setPdfLoadError, downloadDoc };
}

/** Modal del visor (documento/video), común a cualquier superficie que use `usePillActivation`. */
export function PillActivationModal({
  viewer, lang,
}: Readonly<{ viewer: ReturnType<typeof usePillActivation>; lang: string }>) {
  const { t } = useTranslation();
  const { openPill, close, pdfUrl, pdfLoadError, setPdfLoadError, downloadDoc } = viewer;
  if (!openPill) return null;

  const openTitle = pickTitle(openPill.titulo, lang);

  return (
    <div className="modal-overlay" onClick={close}>
      <div className="home-doc-popup" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="home-doc-popup-close"
          aria-label={t('common.close')}
          onClick={close}
        >
          ✕
        </button>
        {pdfUrl && (
          <button
            type="button"
            className="home-doc-popup-download"
            aria-label={t('common.download')}
            title={t('common.download')}
            onClick={() => downloadDoc(openTitle)}
          >
            <i className="fas fa-download" aria-hidden="true" />
          </button>
        )}
        <h3>{openTitle}</h3>
        {openPill.tipo === 'video' && openPill.url && (
          <div className="home-doc-popup-video">
            <iframe src={toEmbedUrl(openPill.url)} title={openTitle} allowFullScreen frameBorder="0" />
          </div>
        )}
        {(openPill.tipo === 'documento' || (openPill.tipo === 'url' && isPdfUrl(openPill.url))) && (
          pdfUrl && !pdfLoadError ? (
            <PdfCanvasViewer
              url={pdfUrl}
              onError={() => setPdfLoadError(true)}
              scrollClassName="home-doc-popup-pdf"
            />
          ) : !pdfUrl ? (
            // Sin `pdfUrl` el documento nunca llegó a resolverse — puede ser restricción de idioma,
            // OU sin alcance, o borrado; el frontend no puede distinguir cuál, así que el mensaje es
            // deliberadamente neutro (no afirma "es por tu idioma" cuando podría no serlo) y no se
            // etiqueta "Error", que sí implica un fallo técnico de red/render.
            <p className="home-doc-popup-pdf-error" role="alert">{t('common.documentNotAvailable')}</p>
          ) : (
            <p className="home-doc-popup-pdf-error" role="alert">{t('common.error')}</p>
          )
        )}
      </div>
    </div>
  );
}
